import { SoundsNotifyService } from '@/app/services/sounds-notify.service';
import { TranslationService } from '@/app/services/translation.service';
import { environment } from '@/environments/environment.development';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzFormModule } from 'ng-zorro-antd/form';
import { EIncidentKind, ETeamGround, MatchIncidents } from '../../models';
import {
  EIncidentVarReason,
  EIncidentVarResult,
  MatchIncident,
  MatchIncidentPlayer,
} from '../../models/match-incidents.model';

@Component({
  selector: 'app-incidents',
  standalone: true,
  imports: [NzFormModule, TranslateModule],
  templateUrl: './incidents.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncidentsComponent {
  private soundEffect = inject(SoundsNotifyService);
  private firstLoad = signal(true);
  private totalPreviousIncidents = signal<number>(0);

  translateService = inject(TranslateService);
  translationService = inject(TranslationService);
  incidents = input.required<MatchIncidents>();

  iconsIncidentFile = `${environment.urlIcon}../../svg/incidents`;

  urlIMG = environment.urlIMG;

  arrIncidents = computed(() => {
    const result = [];

    const transformIncidents = (incidents: MatchIncident[] | null) => {
      if (!incidents) return [];

      return incidents.flatMap((incident) => {
        // Si NO es VAR, regresa tal cual
        if (incident.Kind !== EIncidentKind.VAR) {
          return [incident];
        }

        // Si es VAR pero ambos son null, no se agrega nada
        if (!incident.VarResult && !incident.VarReason) {
          return [];
        }

        // Si tiene VarResult y VarReason, las "desdoblamos" en 2 incidencias
        const transformed: MatchIncident[] = [];

        if (incident.VarReason) {
          transformed.push({
            ...incident,
            VarResult: null, // nos quedamos solo con la reason
          });
        }

        if (incident.VarResult) {
          transformed.push({
            ...incident,
            VarReason: null, // nos quedamos solo con el result
          });
        }

        return transformed;
      });
    };

    if (
      this.incidents().FirstHalfIncidents !== null &&
      this.incidents().FirstHalfIncidents.length > 0
    ) {
      result.push({
        title: this.translateService.instant('FirstHalf'),
        incidents: transformIncidents(
          this.incidents().FirstHalfIncidents.filter(
            (incident) => incident.Kind !== EIncidentKind.MIDFIELD
          )
        ),
      });
    }
    if (
      this.incidents().SecondHalfIncidents !== null &&
      this.incidents().SecondHalfIncidents.length > 0
    ) {
      result.push({
        title: this.translateService.instant('SecondHalf'),
        incidents: transformIncidents(this.incidents().SecondHalfIncidents),
      });
    }

    if (
      this.incidents().OvertimeIncidents !== null &&
      this.incidents().OvertimeIncidents.length > 0
    ) {
      result.push({
        title: this.translateService.instant('Overtime'),
        incidents: transformIncidents(this.incidents().OvertimeIncidents),
      });
    }

    if (
      this.incidents().PenaltyShootOutIncidents !== null &&
      this.incidents().PenaltyShootOutIncidents.length > 0
    ) {
      result.push({
        title: this.translateService.instant('Penalty'),
        incidents: transformIncidents(
          this.incidents().PenaltyShootOutIncidents
        ),
      });
    }

    return result;
  });

  constructor() {
    effect(
      () => {
        const incidents = this.incidents();

        if (!incidents) return;

        const allIncidents = [
          ...(incidents.FirstHalfIncidents || []),
          ...(incidents.SecondHalfIncidents || []),
          ...(incidents.OvertimeIncidents || []),
          ...(incidents.PenaltyShootOutIncidents || []),
        ];

        const totalNow = allIncidents.length;

        if (!this.firstLoad() && totalNow > this.totalPreviousIncidents()) {
          const newIncident = allIncidents[allIncidents.length - 1];
          const kind = newIncident.Kind;

          switch (kind) {
            case EIncidentKind.GOAL: // 1
            case EIncidentKind.OWN_GOAL:
              // this.soundEffect.playGold();
              break;
            case EIncidentKind.CARD_UPGRADE:
            case EIncidentKind.CORNER:
            case EIncidentKind.EXTRA_TIME:
            case EIncidentKind.OVERTIME_IS_OVER:
            case EIncidentKind.PENALTY:
            case EIncidentKind.PENALTY_KICK_ENDED:
            case EIncidentKind.PENALTY_MISSED:
            case EIncidentKind.RED_CARD: // 4
            case EIncidentKind.SUBSTITUTION: // 9
            case EIncidentKind.VAR:
            case EIncidentKind.YELLOW_CARD: // 3
              // this.soundEffect.playRefereeBegin();
              break;
            case EIncidentKind.END: // 12
            case EIncidentKind.MIDFIELD:
              // this.soundEffect.playRefereeEnd();
              break;

            default:
              break;
          }
        }

        this.totalPreviousIncidents.set(totalNow);
        this.firstLoad.set(false);
      },
      { allowSignalWrites: true }
    );
  }

  awayTeam = ETeamGround.AWAY;
  isNeutral = ETeamGround.NEUTRAL;
  isPenalty = EIncidentKind.PENALTY;
  isOWNGOAL = EIncidentKind.OWN_GOAL;
  isEND = EIncidentKind.END;
  isVAR = EIncidentKind.VAR;
  isSustitution = EIncidentKind.SUBSTITUTION;

  isEndPeriod = [
    EIncidentKind.END,
    EIncidentKind.MIDFIELD,
    EIncidentKind.VAR,
    EIncidentKind.EXTRA_TIME,
    EIncidentKind.PENALTY_KICK_ENDED,
    EIncidentKind.OVERTIME_IS_OVER,
  ];

  isCard = [
    EIncidentKind.YELLOW_CARD,
    EIncidentKind.RED_CARD,
    EIncidentKind.CARD_UPGRADE,
  ];

  isPenalMissed = EIncidentKind.PENALTY_MISSED;
  isPenaltyCase = [
    EIncidentKind.PENALTY,
    EIncidentKind.PENALTY_KICK_ENDED,
    EIncidentKind.PENALTY_MISSED,
    EIncidentKind.EXTRA_TIME,
    EIncidentKind.OVERTIME_IS_OVER,
  ];
  isGOAL = [EIncidentKind.GOAL, EIncidentKind.OWN_GOAL, ...this.isPenaltyCase];

  getImgByKind(kind: EIncidentKind) {
    switch (kind) {
      case EIncidentKind.GOAL: // 1
        return `${this.iconsIncidentFile}/${EIncidentKind.GOAL}.svg`;
      case EIncidentKind.CARD_UPGRADE:
        return `${this.iconsIncidentFile}/${EIncidentKind.CARD_UPGRADE}.svg`;
      case EIncidentKind.CORNER:
        return `${this.iconsIncidentFile}/${EIncidentKind.CORNER}.svg`;
      case EIncidentKind.END: // 12
        return `${this.iconsIncidentFile}/${EIncidentKind.END}.svg`;
      case EIncidentKind.EXTRA_TIME:
        return `${this.iconsIncidentFile}/${EIncidentKind.EXTRA_TIME}.svg`;
      case EIncidentKind.MIDFIELD:
        return `${this.iconsIncidentFile}/${EIncidentKind.MIDFIELD}.svg`;
      case EIncidentKind.OVERTIME_IS_OVER:
        return `${this.iconsIncidentFile}/${EIncidentKind.OVERTIME_IS_OVER}.svg`;
      case EIncidentKind.OWN_GOAL:
        return `${this.iconsIncidentFile}/${EIncidentKind.OWN_GOAL}.svg`;
      case EIncidentKind.PENALTY:
        return `${this.iconsIncidentFile}/${EIncidentKind.PENALTY}.svg`;
      case EIncidentKind.PENALTY_KICK_ENDED:
        return `${this.iconsIncidentFile}/${EIncidentKind.PENALTY_KICK_ENDED}.svg`;
      case EIncidentKind.PENALTY_MISSED:
        return `${this.iconsIncidentFile}/${EIncidentKind.PENALTY_MISSED}.svg`;
      case EIncidentKind.RED_CARD: // 4
        return `${this.iconsIncidentFile}/${EIncidentKind.RED_CARD}.svg`;
      case EIncidentKind.SUBSTITUTION: // 9
        return `${this.iconsIncidentFile}/${EIncidentKind.SUBSTITUTION}.svg`;
      case EIncidentKind.VAR:
        return `${this.iconsIncidentFile}/${EIncidentKind.VAR}.svg`;
      case EIncidentKind.YELLOW_CARD: // 3
        return `${this.iconsIncidentFile}/${EIncidentKind.YELLOW_CARD}.svg`;

      default:
        return this.urlIMG + 'default.png';
    }
  }

  getTextEndPeriod(kind: EIncidentKind) {
    switch (kind) {
      case EIncidentKind.END:
        return this.translateService.instant('end_match');
      case EIncidentKind.MIDFIELD:
        return this.translateService.instant('halftime');
      case EIncidentKind.VAR:
        return this.translateService.instant('VAR');
      case EIncidentKind.PENALTY_KICK_ENDED:
        return this.translateService.instant('Penalty');

      case EIncidentKind.OVERTIME_IS_OVER:
        return this.translateService.instant('OvertimeIsOver');
      case EIncidentKind.EXTRA_TIME:
        return this.translateService.instant('OVERTIME');

      default:
        return '';
    }
  }

  getTextVarReason(varReason: EIncidentVarReason | null) {
    switch (varReason) {
      case EIncidentVarReason.CARD_UPGRADE:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_CARD_UPGRADE'
        );
      case EIncidentVarReason.RED_CARD_GIVEN:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_RED_CARD_GIVEN'
        );

      case EIncidentVarReason.GOAL_AWARDED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_GOAL_AWARDED'
        );
      case EIncidentVarReason.GOAL_NOT_AWARDED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_GOAL_NOT_AWARDED'
        );

      case EIncidentVarReason.MISTAKEN_IDENTITY:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_MISTAKEN_IDENTITY'
        );

      case EIncidentVarReason.PENALTY_AWARDED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_PENALTY_AWARDED'
        );
      case EIncidentVarReason.PENALTY_NOT_AWARDED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_PENALTY_NOT_AWARDED'
        );

      case EIncidentVarReason.OTHER:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_REASON_OTHER'
        );

      default:
        return '';
    }
  }

  getTextVarResult(varResult: EIncidentVarResult | null) {
    switch (varResult) {
      case EIncidentVarResult.CARD_UPGRADE_CANCELED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_CARD_UPGRADE_CANCELED'
        );
      case EIncidentVarResult.CARD_UPGRADE_CONFIRMED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_CARD_UPGRADE_CONFIRMED'
        );
      case EIncidentVarResult.RED_CARD_CANCELED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_RED_CARD_CANCELED'
        );
      case EIncidentVarResult.RED_CARD_CONFIRMED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_RED_CARD_CONFIRMED'
        );

      case EIncidentVarResult.GOAL_CANCELED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_GOAL_CANCELED'
        );
      case EIncidentVarResult.GOAL_CONFIRMED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_GOAL_CONFIRMED'
        );

      case EIncidentVarResult.ORIGINAL_DECISION:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_ORIGINAL_DECISION'
        );
      case EIncidentVarResult.ORIGINAL_DECISION_CHANGED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_ORIGINAL_DECISION_CHANGED'
        );

      case EIncidentVarResult.PENALTY_CANCELED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_PENALTY_CANCELED'
        );
      case EIncidentVarResult.PENALTY_CONFIRMED:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_PENALTY_CONFIRMED'
        );

      case EIncidentVarResult.OTHER:
        return this.translateService.instant(
          'MATCH_DETAILS_INCIDENTS.VAR_RESULT_UNKNOWN'
        );

      default:
        return '';
    }
  }

  getVarReasonResult(
    varReason: EIncidentVarReason | null,
    varResult: EIncidentVarResult | null
  ): string {
    if (!varReason && !varResult) return '';

    if (varResult) return this.getTextVarResult(varResult);
    else return this.getTextVarReason(varReason);
  }

  getTraduccionName(
    traduccionByApi: Record<string, string> | null,
    defaultName: string
  ) {
    return this.translationService.getTraduccionName(
      traduccionByApi,
      defaultName
    );
  }

  getNameIncident(player: MatchIncidentPlayer) {
    if (player.ShortName) {
      return this.getTraduccionName(
        player.ShortNameTranslation,
        player.ShortName
      );
    }

    return this.getTraduccionName(player.NameTranslation, player.Name);
  }
}
